import { useCallback } from 'react'
import { useHistory, useParams } from 'react-router-dom'

import { routes, useAnalytics } from '@sketch/modules-common'
import { usePreview } from '../../shares/views/PreviewView/PreviewContext'
import type { GoToArtboardEventActionType } from '@sketch/canvas'

/**
 * Returns a navigateToSection callback for the preview context.
 * Navigates to the PREVIEW_PAGE_SECTION route using the current previewID and
 * pageUUID - the Preview counterpart of useNavigateToSection.
 *
 * pageUUID is not present in the URL on the root preview route, so we fall back
 * to the current page from PreviewContext (or the first page of the preview).
 */
export function useNavigateToPreviewSection() {
  const history = useHistory()
  const { previewID, pageUUID } = useParams<{
    previewID?: string
    pageUUID?: string
  }>()
  const previewContext = usePreview()
  const { trackEvent } = useAnalytics()

  const safeguardedPageUUID =
    pageUUID ??
    previewContext?.currentPage?.uuid ??
    previewContext?.preview.pages.entries[0]?.uuid

  return useCallback(
    (sectionUUID: string, actionType: GoToArtboardEventActionType) => {
      const pageUUID = safeguardedPageUUID
      if (!previewID || !pageUUID) return

      trackEvent('CANVAS - open artboard', { actionType })
      history.push({
        pathname: routes.PREVIEW_PAGE_SECTION.create({
          previewID,
          pageUUID,
          sectionUUID,
        }),
        hash: history.location.hash,
      })
    },
    [history, previewID, safeguardedPageUUID, trackEvent]
  )
}
